import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TrendUp, TrendDown, Minus, ArrowsLeftRight } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { BACKEND_URL } from '../config';

const MonthComparison = () => {
  const [monthA, setMonthA] = useState('');
  const [monthB, setMonthB] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);

  const fetchAvailableMonths = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/analytics`, { withCredentials: true });
      const months = (data.chart_data || []).map(d => d.month).filter(Boolean);
      setAvailableMonths(months);
      if (months.length >= 2) {
        setMonthA(months[months.length - 2]);
        setMonthB(months[months.length - 1]);
      }
    } catch (err) { console.error(err.message);
      // silent
    }
  }, []);

  useEffect(() => { fetchAvailableMonths(); }, [fetchAvailableMonths]);

  const handleCompare = async () => {
    if (!monthA || !monthB) { toast.error('Select two months'); return; }
    if (monthA === monthB) { toast.error('Select different months'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/analytics/compare-months`, { month_a: monthA, month_b: monthB }, { withCredentials: true });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Comparison failed');
    } finally {
      setLoading(false);
    }
  };

  const formatMonth = (m) => {
    if (!m) return '';
    const [y, mo] = m.split('-');
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${names[parseInt(mo)-1]} ${y}`;
  };

  const chartData = result
    ? result.client_comparison.slice(0, 15).map(c => ({
        name: c.name.length > 12 ? c.name.slice(0, 12) + '...' : c.name,
        [formatMonth(result.month_a.month)]: c.month_a_revenue,
        [formatMonth(result.month_b.month)]: c.month_b_revenue,
      }))
    : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <ArrowsLeftRight size={20} weight="regular" />
        <span className="font-semibold text-sm">Month vs Month</span>
      </div>

      <div className="bg-card border border-border rounded-sm p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={monthA} onValueChange={setMonthA}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="month-a-select"><SelectValue placeholder="Month A" /></SelectTrigger>
            <SelectContent>
              {availableMonths.map(m => <SelectItem key={m} value={m}>{formatMonth(m)}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-muted-foreground text-sm font-bold">vs</span>
          <Select value={monthB} onValueChange={setMonthB}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="month-b-select"><SelectValue placeholder="Month B" /></SelectTrigger>
            <SelectContent>
              {availableMonths.map(m => <SelectItem key={m} value={m}>{formatMonth(m)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={handleCompare} disabled={loading || !monthA || !monthB} className="rounded-sm" data-testid="compare-months-btn">
            {loading ? 'Comparing...' : 'Compare'}
          </Button>
        </div>
      </div>

      {result && (
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-card border border-border rounded-sm p-4" data-testid="month-a-summary">
              <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1">{formatMonth(result.month_a.month)}</div>
              <div className="text-xl font-bold tracking-tight">₹{result.month_a.total_revenue.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground mt-1">{result.month_a.transactions} txns · {result.month_a.active_clients} clients</div>
            </div>
            <div className="bg-card border border-border rounded-sm p-4 flex items-center justify-center" data-testid="month-change-pct">
              <div className="text-center">
                {result.change_pct > 0
                  ? <TrendUp size={24} weight="bold" className="text-accent mx-auto" />
                  : result.change_pct < 0
                    ? <TrendDown size={24} weight="bold" className="text-destructive mx-auto" />
                    : <Minus size={24} weight="bold" className="text-muted-foreground mx-auto" />}
                <div className={`text-2xl font-bold mt-1 ${result.change_pct > 0 ? 'text-accent' : result.change_pct < 0 ? 'text-destructive' : ''}`}>
                  {result.change_pct > 0 ? '+' : ''}{result.change_pct}%
                </div>
                <div className="text-xs text-muted-foreground">Change</div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-sm p-4" data-testid="month-b-summary">
              <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1">{formatMonth(result.month_b.month)}</div>
              <div className="text-xl font-bold tracking-tight">₹{result.month_b.total_revenue.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground mt-1">{result.month_b.transactions} txns · {result.month_b.active_clients} clients</div>
            </div>
          </div>

          {/* Chart */}
          {chartData.length > 0 && (
            <div className="bg-card border border-border rounded-sm p-5">
              <div className="text-sm font-semibold mb-4">Per-Client Revenue Comparison</div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" stroke="hsl(var(--foreground))" style={{ fontSize: '11px' }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" width={100} stroke="hsl(var(--foreground))" style={{ fontSize: '10px' }} />
                  <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '0.25rem', fontSize: '12px' }} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey={formatMonth(result.month_a.month)} fill="hsl(var(--muted-foreground))" radius={[0, 2, 2, 0]} />
                  <Bar dataKey={formatMonth(result.month_b.month)} fill="hsl(var(--accent))" radius={[0, 2, 2, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Client table */}
          <div className="bg-card border border-border rounded-sm overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Client</th>
                  <th className="text-right py-3 px-4 text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">{formatMonth(result.month_a.month)}</th>
                  <th className="text-right py-3 px-4 text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">{formatMonth(result.month_b.month)}</th>
                  <th className="text-right py-3 px-4 text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Change</th>
                </tr>
              </thead>
              <tbody>
                {result.client_comparison.map(c => (
                  <tr key={c.client_id} className="border-b border-border hover:bg-muted/50" data-testid={`month-compare-row-${c.client_id}`}>
                    <td className="py-2.5 px-4 text-sm font-medium">{c.name}</td>
                    <td className="py-2.5 px-4 text-right text-sm text-muted-foreground">₹{c.month_a_revenue.toLocaleString()}</td>
                    <td className="py-2.5 px-4 text-right text-sm">₹{c.month_b_revenue.toLocaleString()}</td>
                    <td className={`py-2.5 px-4 text-right text-sm font-semibold ${c.change_pct > 0 ? 'text-accent' : c.change_pct < 0 ? 'text-destructive' : ''}`}>
                      {c.change_pct > 0 ? '+' : ''}{c.change_pct}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthComparison;
